import { Permission } from '@act/shared/authorization';
import {
  MemberStatusChangeRequestAuditForm,
  MemberStatusChangeRequestAuditFormSchema,
  MemberStatusChangeRequestDto,
  MemberStatusChangeRequestForm,
  MemberStatusChangeRequestFormSchema,
  MemberStatusDto,
} from '@act/shared/data-transfer-objects';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormBuilder } from '@act/storybook';
import { membersRequests } from 'redux/requests/members.request';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice.slice';
import {
  getMemberStatuses,
  membersAppSelectors,
} from 'redux/slices/memberSlice.slice';
import { memberStatusChangeRequestSlice } from 'redux/slices/memberStatusChangeRequestSlice.slice';
import { userAppSelectors } from 'redux/slices/usersSlice.slice';
import { useAppDispatch } from 'redux/storeHooks';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { reemaColors } from 'toolkit/styles/colors';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import { Button } from '@act/storybook';
import { getEnvironment } from 'toolkit/environment/environment/environment.util';
import { SelectOption } from '@act/storybook';
import { useToast } from '../app/ReusableToast';
import { getMinDateForStatusChangeRequest } from 'utilities/date-validation/statusChangeRequestDates.util';
import useUnsavedChangesDialog from './hooks/useUnsavedChangesDialog.hook';
import { createMemberStatusChangeRequestFormConfig } from './MemberStatusChangeRequestFormConfig';
import { TrackingProvider } from 'react-components/tracking';
import type {
  MemberStatusChangeRequestRequest,
  UpdateMemberStatusChangeRequestRequest,
} from '@act/shared/types';

export type CreateMemberStatusChangeRequestPreProcessingForm = Omit<
  MemberStatusChangeRequestRequest,
  'effectiveAt'
> & {
  effectiveAt: string; // 'yyyy-MM-dd'
  auditorComment?: string; // Add this field
};

export type MemberStatusChangeRequestCallbackInput = {};

export interface MemberStatusChangeRequestDialogUniqueProps {
  memberId?: string;
  interactionId?: number;
  effectiveAt?: number;
  memberStatusChangeRequest?: MemberStatusChangeRequestDto;
  isEdit?: boolean;
}

export type MemberStatusChangeRequestProps = GenericDialogProps<boolean> &
  MemberStatusChangeRequestDialogUniqueProps;

const MemberStatusChangeRequestDialog = ({
  memberId,
  effectiveAt,
  interactionId,
  memberStatusChangeRequest,
  open,
  setOpen,
  callback,
  isEdit,
}: MemberStatusChangeRequestProps) => {
  const environment = getEnvironment();
  const algorithmStartDate = DateTime.fromFormat(
    environment.memberStatusAlgorithmStartDate,
    'yyyy-MM-dd'
  );
  const toast = useToast();
  const dispatch = useAppDispatch();
  const memberStatuses = membersAppSelectors.useSelectAllMemberStatuses();
  const member = membersAppSelectors.useSelectMemberById(
    memberId || memberStatusChangeRequest.memberId
  );

  const selectableOptions =
    membersAppSelectors.useSelectSelectableMemberStatuses({
      memberId: memberId,
    });

  const author = userAppSelectors.useSelectUserById(
    memberStatusChangeRequest?.creatorId
  );

  const canRequestAdminLevelStatuses =
    actingUserAppSelectors.useUserHasPermission(
      Permission.CAN_REQUEST_ADMIN_LEVEL_STATUSES
    );
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const [selectedOption, setSelectedOption] = useState<MemberStatusDto>(null);
  const [minDate, setMinDate] = useState<DateTime>(DateTime.now());

  const safeSetMinDate = minDate => {
    // Ensure minDate is not before the algorithm start date.
    if (minDate < algorithmStartDate) {
      setMinDate(algorithmStartDate);
    } else {
      setMinDate(minDate);
    }
  };

  const [selectOptions, setSelectOptions] = useState<SelectOption<number>[]>(
    []
  );

  const canAuditStatusChangeRequest =
    membersAppSelectors.useHasPermissionOnMember(
      memberId,
      Permission.CAN_AUDIT_MEMBER_STATUS_CHANGE_REQUESTS
    );

  const isAuditingRequest =
    memberStatusChangeRequest && canAuditStatusChangeRequest;

  const handleClose = useCallback((dialogText?: string) => {
    if (dialogText) {
      toast(dialogText);
    }
    setOpen(false);
    callback(true);
  }, []);

  const { formState, control, watch, handleSubmit } = useForm<
    MemberStatusChangeRequestForm | MemberStatusChangeRequestAuditForm
  >({
    resolver: zodResolver(
      isAuditingRequest
        ? MemberStatusChangeRequestAuditFormSchema
        : MemberStatusChangeRequestFormSchema
    ),
    defaultValues: {
      id: memberStatusChangeRequest?.id ?? null,
      memberId: memberStatusChangeRequest?.memberId ?? memberId,
      effectiveAt: memberStatusChangeRequest?.effectiveAt
        ? DateTime.fromMillis(memberStatusChangeRequest.effectiveAt).toFormat(
            'yyyy-MM-dd'
          )
        : effectiveAt
        ? DateTime.fromMillis(effectiveAt).toFormat('yyyy-MM-dd')
        : minDate.toFormat('yyyy-MM-dd'),
      interactionId: memberStatusChangeRequest?.interactionId ?? interactionId,
      memberStatusId: memberStatusChangeRequest?.memberStatusId ?? null,
      creatorComment: memberStatusChangeRequest?.creatorComment ?? null,
      approval: memberStatusChangeRequest?.approvedAt
        ? 'approve'
        : memberStatusChangeRequest?.deniedAt
        ? 'deny'
        : null,
      auditorComment: memberStatusChangeRequest?.auditorComment ?? null,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    const minDate = getMinDateForStatusChangeRequest();
    safeSetMinDate(minDate);
  }, [memberStatusChangeRequest]);

  const { isDirty, isValid } = formState;

  const memberStatusId = watch('memberStatusId');
  const currentEffectiveAt = watch('effectiveAt');

  const [changeRequestPayload, isCreatingChangeRequest, createChangeRequest] =
    useApiCall(membersRequests.postMemberStatusChangeRequest);

  const [patchRequestPayload, isPatchingChangeRequest, patchChangeRequest] =
    useApiCall(membersRequests.patchMemberStatusChangeRequest);

  // Ref to prevent double-clicking
  const isSubmittingRef = useRef(false);

  async function onSubmit(
    data: MemberStatusChangeRequestForm | MemberStatusChangeRequestAuditForm
  ) {
    if (!isValid) return;

    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    if (memberStatusChangeRequest) {
      const requestBody =
        isEdit && !isAuditingRequest
          ? {
              ...data,
              effectiveAt: DateTime.fromFormat(
                data.effectiveAt,
                'yyyy-MM-dd'
              ).toMillis(),
              approval: null,
            }
          : {
              ...data,
              effectiveAt: DateTime.fromFormat(
                data.effectiveAt,
                'yyyy-MM-dd'
              ).toMillis(),
              approval: data?.approval,
            };

      patchChangeRequest({
        params: { id: memberStatusChangeRequest.id },
        body: requestBody as UpdateMemberStatusChangeRequestRequest,
      });
    } else {
      createChangeRequest({
        params: null,
        body: {
          ...data,
          // auto approve for managers
          approval: canAuditStatusChangeRequest ? 'approve' : null,
          effectiveAt: DateTime.fromFormat(
            data.effectiveAt,
            'yyyy-MM-dd'
          ).toMillis(),
        },
      });
    }
  }

  useEffect(() => {
    const theChange = patchRequestPayload || changeRequestPayload;
    let dialogText: string = null;
    if (theChange && theChange.memberStatusChangeRequest) {
      void dispatch(
        memberStatusChangeRequestSlice.actions.memberStatusChangeRequestCreated(
          theChange
        )
      );
      if (changeRequestPayload) {
        if (
          changeRequestPayload.memberStatusChangeRequest.approvedAt ||
          changeRequestPayload.memberStatusChangeRequest.deniedAt
        ) {
          dialogText = 'Your status change request is complete';
        } else {
          dialogText = 'Your status change will be made pending review.';
        }
      } else {
        if (
          patchRequestPayload.memberStatusChangeRequest.approvedAt ||
          patchRequestPayload.memberStatusChangeRequest.deniedAt
        ) {
          dialogText = 'You have successfully audited this request.';
        } else {
          dialogText = 'You have successfully edited this request.';
        }
      }
      void handleClose(dialogText);
    }
  }, [changeRequestPayload, dispatch, handleClose, patchRequestPayload]);

  // Reset the submitting ref when API calls complete
  useEffect(() => {
    if (changeRequestPayload || patchRequestPayload) {
      isSubmittingRef.current = false;
    }
  }, [changeRequestPayload, patchRequestPayload]);

  useEffect(() => {
    if (!memberStatuses.length) {
      dispatch(getMemberStatuses({}));
    }
    if (memberStatusChangeRequest) {
      findSelectedOption();
    }
  }, []);

  useEffect(() => {
    findSelectedOption();
  }, [memberStatusId]);

  const findSelectedOption = () => {
    const selectedOption = memberStatuses.find(
      option => option.id == memberStatusId
    );
    setSelectedOption(selectedOption);
  };

  // Form configuration for FormBuilder
  const formConfig = createMemberStatusChangeRequestFormConfig({
    isAuditingRequest,
    selectOptions,
    selectedOption,
    minDate,
    member,
    author,
    memberStatusChangeRequest,
    currentEffectiveAt,
  });

  useEffect(() => {
    const noDepreciatedStatuses = selectableOptions.filter(
      status => !status.memberStatus.key.includes('deprecated')
    );

    const filteredOptions = canRequestAdminLevelStatuses
      ? noDepreciatedStatuses
      : noDepreciatedStatuses.filter(o => o.isSelectable);
    setSelectOptions(
      filteredOptions.map(option => {
        return {
          label: (
            <>
              {!option.isSelectable && (
                <AdminPanelSettingsIcon fontSize="small" />
              )}
              {option.memberStatus.name}
            </>
          ),
          value: option.memberStatus.id,
        };
      })
    );
  }, [canRequestAdminLevelStatuses, memberStatuses]);

  let submitText = 'Send request';
  if (memberStatusChangeRequest && isAuditingRequest) {
    submitText = 'Send review';
  }

  const handleFormClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        setOpen(false);
        callback(true);
      });
    } else {
      setOpen(false);
      callback(true);
    }
  };

  return (
    <TrackingProvider contextName="member-status-change-request-dialog">
      <div style={{ width: '600px' }}>
        <Dialog
          open={open}
          onClose={handleFormClose}
          fullScreen
          hideBackdrop
          sx={{
            marginTop: '51px',
            '& MuiModal-backdrop': {
              marginTop: '51px',
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '44px',
              padding: '18px 36px',
              boxShadow:
                '0px 1px 2px rgba(0, 0, 0, 0.16), 0px 1px 3px 1px rgba(0, 0, 0, 0.08)',
              zIndex: '2',
            }}
          >
            <IconButton onClick={() => handleFormClose()}>
              <ArrowBackIcon />
            </IconButton>
            Member Status Change Request
          </DialogTitle>

          <DialogContent
            sx={{
              display: 'grid',
              justifyContent: 'center',
              paddingTop: '12px !important',
              backgroundColor: reemaColors.lightBackground,
              zIndex: '1',
            }}
          >
            <Card
              sx={{
                height: 'min-content',
                maxWidth: '570px',
              }}
            >
              <CardContent
                sx={{
                  padding: '24px 24px 0',
                  '&:last-child': {
                    paddingBottom: 0,
                  },
                }}
              >
                <form onSubmit={handleSubmit(onSubmit)}>
                  {isAuditingRequest && (
                    <div className="form-header">
                      <Typography variant="h6">
                        Approve or deny the status change request.
                      </Typography>
                      <Typography variant="body1">
                        Evaluate whether there is necessity or cause for a
                        change in status or guide assignment for the member.
                      </Typography>
                    </div>
                  )}
                  {!isAuditingRequest && (
                    <div className="form-header">
                      <Typography variant="h6">
                        We need a little info about your request.
                      </Typography>
                      <Typography variant="body1">
                        Providing these details will help us resolve the
                        request. It may take a few days for a change to be made.
                      </Typography>
                    </div>
                  )}
                  <div className="form-body">
                    <FormBuilder
                      config={formConfig}
                      control={control}
                      formState={formState}
                    />
                  </div>
                  <CardActions
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'right',
                      padding: '8px 24px',
                    }}
                  >
                    <Button
                      onClick={handleFormClose}
                      disabled={
                        isCreatingChangeRequest || isPatchingChangeRequest
                      }
                      trackingName="cancel-button"
                    >
                      Cancel
                    </Button>
                    <Button
                      disabled={
                        !isValid ||
                        isCreatingChangeRequest ||
                        isPatchingChangeRequest ||
                        isSubmittingRef.current
                      }
                      type={'submit'}
                      variant="contained"
                      debounced
                      trackingName="submit-status-change-button"
                    >
                      {submitText}
                    </Button>
                  </CardActions>
                </form>
              </CardContent>
            </Card>
          </DialogContent>
        </Dialog>
      </div>
    </TrackingProvider>
  );
};

export default MemberStatusChangeRequestDialog;
